// One step of the walkthrough, played on its own (the in-app OnboardingGuide): the same
// engine, screens and narration as /tutorial-demo, limited to the given scene ids — so each
// step's "video" is ~10-15s instead of the whole tutorial. Desktop layout (16:10 stage).
import React, {useEffect, useMemo, useRef, useState} from 'react'
import {SCENES, STAGE_W, STAGE_H} from './demoScript'
import {LoginScreen, AppScreen} from './DemoScreens'
import {useDemoEngine} from './useDemoEngine'
import '../groups/index.scss'
import '../../components/OnboardingWizard.scss'
import '../../components/SupportWidget/SupportWidget.scss'
import './TutorialDemo.scss'

type Props = {
  sceneIds: string[]
  autoPlay?: boolean
  onPlay?: () => void
  onEnd?: () => void
}

const StepDemo: React.FC<Props> = ({sceneIds, autoPlay, onPlay, onEnd}) => {
  const scenes = useMemo(() => SCENES.filter((s) => sceneIds.includes(s.id)), [sceneIds.join(',')])
  const {
    t,
    total,
    local,
    scene,
    state,
    playing,
    started,
    play,
    seek,
    stageRef,
    contentRef,
    cursorRef,
    rippleRef,
    spotRef,
  } = useDemoEngine(scenes, STAGE_W, STAGE_H)
  const [scale, setScale] = useState(0.3)
  const frameRef = useRef<HTMLDivElement>(null)
  const ended = started && !playing && t >= total - 50

  useEffect(() => {
    const el = frameRef.current
    if (!el) return
    const ro = new ResizeObserver(() => setScale(el.clientWidth / STAGE_W))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  useEffect(() => {
    if (autoPlay) {
      play()
      onPlay?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const endedRef = useRef(false)
  useEffect(() => {
    if (ended && !endedRef.current) {
      endedRef.current = true
      onEnd?.()
    }
    if (!ended) endedRef.current = false
  }, [ended, onEnd])

  const start = () => {
    if (ended) seek(0)
    play()
    onPlay?.()
  }

  const words = scene.text.split(' ')
  const spoken = Math.floor(Math.min(1, local / scene.audioMs) * words.length)

  return (
    <div
      className='td-page td-embed'
      dir='rtl'
      style={{background: 'transparent', padding: 0, minHeight: 0, flex: 'none'}}
    >
      <div className='td-player' style={{maxWidth: '100%', margin: 0}}>
        <div
          className='td-frame'
          ref={frameRef}
          style={{height: STAGE_H * scale, borderRadius: 10}}
          dir='ltr'
        >
          <div
            className='td-stage'
            ref={stageRef}
            style={{width: STAGE_W, height: STAGE_H, transform: `scale(${scale})`}}
          >
            {state.screen === 'login' ? (
              <LoginScreen s={state} />
            ) : (
              <AppScreen s={state} contentRef={contentRef} />
            )}
            <div className='td-spot' ref={spotRef} />
            <div className='td-ripple' ref={rippleRef} />
            <div className='td-cursor' ref={cursorRef}>
              <svg width='28' height='28' viewBox='0 0 24 24'>
                <path
                  d='M4 2l15 11.2-6.6 1.1 3.9 7.3-2.9 1.5-3.9-7.4L4 20.5z'
                  fill='#111'
                  stroke='#fff'
                  strokeWidth='1.4'
                  strokeLinejoin='round'
                />
              </svg>
            </div>
          </div>
          {(!started || ended || !playing) && (
            <button
              className='td-paused'
              onClick={start}
              aria-label='נגן'
              style={{background: 'rgba(15,22,41,.35)'}}
            >
              <span className='td-poster-play small'>
                {ended ? (
                  <i
                    className='bi bi-arrow-counterclockwise'
                    style={{fontSize: 30, color: '#fff'}}
                  />
                ) : (
                  <svg viewBox='0 0 24 24' width='34' height='34'>
                    <path d='M8 5v14l11-7z' fill='#fff' />
                  </svg>
                )}
              </span>
            </button>
          )}
        </div>
        {started && (
          <div className='td-caption' style={{padding: '6px 2px 0'}}>
            <p style={{fontSize: 13, margin: 0}}>
              {words.map((w, i) => (
                <span key={i} className={i < spoken ? 'spoken' : ''}>
                  {w}{' '}
                </span>
              ))}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default StepDemo
